/**
 * 图片加密/解密工具
 *
 * 使用AES-GCM加密算法，Base58编码
 *
 * 文件名格式：iv_1_{keyId}_b58_{encodedName}_{randomSuffix}.bin
 */

// Base58 字符集
const B58_CHARS = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
const B58_BASE = 58n;

// 默认密钥ID
const DEFAULT_KEY_ID = 'n001';

// 密钥缓存
const _keyCache = new Map<string, string>();
const _decryptedBlobCache = new Map<string, Promise<Blob>>();
const MAX_DECRYPTED_BLOB_CACHE_SIZE = 300;
let _aesKeyPromise: Promise<CryptoKey> | null = null;
const MIME_TYPE_LABEL_MAP: Record<string, string> = {
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'image/webp': 'WEBP',
  'image/gif': 'GIF',
  'image/avif': 'AVIF',
  'image/bmp': 'BMP',
  'image/tiff': 'TIFF',
};

/**
 * Base64字符串转Uint8Array
 */
function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * 导入AES密钥
 */
async function importAesKey(): Promise<CryptoKey> {
  if (_aesKeyPromise) {
    return _aesKeyPromise;
  }

  // 使用固定密钥，不再依赖keyId
  const fixedKeyBase64 = 'Tbdb32SgtTKBh0vBfXgw8mNAf9LbiUoVpni3OlRwqgw=';
  const keyBytes = base64ToBytes(fixedKeyBase64);

  _aesKeyPromise = crypto.subtle.importKey(
    'raw',
    new Uint8Array(keyBytes),
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt'],
  );
  return _aesKeyPromise;
}

/**
 * 字符串转字节数组
 */
function stringToBytes(str: string): Uint8Array {
  return new TextEncoder().encode(str);
}

/**
 * 字节数组转字符串
 */
function bytesToString(bytes: Uint8Array): string {
  return new TextDecoder().decode(bytes);
}

/**
 * 字节数组转BigInt
 */
function bytesToBigInt(bytes: Uint8Array): bigint {
  if (bytes.length === 0) return 0n;
  let result = 0n;
  for (const byte of bytes) {
    result = (result << 8n) | BigInt(byte);
  }
  return result;
}

/**
 * BigInt转字节数组
 */
function bigIntToBytes(value: bigint, expectedLength: number): Uint8Array {
  if (expectedLength <= 0) return new Uint8Array(0);

  const bytes = new Uint8Array(expectedLength);
  let remaining = value;

  for (let i = expectedLength - 1; i >= 0; i--) {
    bytes[i] = Number(remaining & 0xffn);
    remaining >>= 8n;
  }

  return bytes;
}

/**
 * Base58编码
 */
export function encodeBase58(input: string): string {
  const bytes = stringToBytes(input);
  if (bytes.length === 0) return '';

  const value = bytesToBigInt(bytes);
  let encoded = '';
  let current = value;

  while (current > 0n) {
    const remainder = Number(current % B58_BASE);
    encoded = B58_CHARS[remainder] + encoded;
    current /= B58_BASE;
  }

  // 添加长度前缀
  return `${bytes.length.toString(36)}.${encoded}`;
}

/**
 * Base58解码
 */
export function decodeBase58(encoded: string): string {
  const [lengthPart, payload] = encoded.split('.');
  if (!lengthPart || payload === undefined) return '';

  const expectedLength = parseInt(lengthPart, 36);
  if (!Number.isFinite(expectedLength) || expectedLength < 0) return '';

  let value = 0n;
  for (const char of payload) {
    const index = B58_CHARS.indexOf(char);
    if (index < 0) return '';
    value = value * B58_BASE + BigInt(index);
  }

  const bytes = bigIntToBytes(value, expectedLength);
  return bytesToString(bytes);
}

/**
 * 从URL提取密钥ID
 */
export function extractKeyIdFromUrl(url: string): string | null {
  try {
    const parsed = new URL(url, window.location.origin);
    const match = parsed.pathname.match(/iv_1_([^_/]+)/);
    return match?.[1] || null;
  } catch {
    return null;
  }
}

/**
 * 判断URL是否为加密图片
 */
export function isEncryptedImageUrl(url?: string | null): boolean {
  if (!url) return false;
  return url.endsWith('.bin') || url.includes('iv_1_');
}

/**
 * 获取默认密钥ID
 */
export function getDefaultKeyId(): string {
  return DEFAULT_KEY_ID;
}

/**
 * 加密文件
 */
export async function encryptFile(file: File): Promise<Blob> {
  const key = await importAesKey();
  const plainBytes = new Uint8Array(await file.arrayBuffer());

  // 生成12字节IV
  const iv = crypto.getRandomValues(new Uint8Array(12));

  // AES-GCM加密
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plainBytes);

  // 分离密文和tag
  const encryptedArray = new Uint8Array(encrypted);
  const ciphertext = encryptedArray.slice(0, -16);
  const tag = encryptedArray.slice(-16);

  // 组合 IV + 密文 + tag
  const payload = new Uint8Array(iv.length + ciphertext.length + tag.length);
  payload.set(iv, 0);
  payload.set(ciphertext, iv.length);
  payload.set(tag, iv.length + ciphertext.length);

  return new Blob([payload], { type: 'application/octet-stream' });
}

/**
 * 解密图片URL
 */
export async function decryptImageUrl(url: string): Promise<string> {
  try {
    const blob = await decryptImageToBlob(url);
    const objectUrl = URL.createObjectURL(blob);

    return objectUrl;
  } catch (error: any) {
    console.error('解密过程中出错:', error);
    throw error;
  }
}

/**
 * 从URL推断MIME类型
 */
export function inferMimeType(url: string): string {
  const match = url.match(/b58_([^_]+)_/);
  if (match) {
    const decoded = decodeBase58(match[1]);
    const ext = decoded.split('.').pop()?.toLowerCase();
    if (ext) {
      const mimeMap: Record<string, string> = {
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        webp: 'image/webp',
        gif: 'image/gif',
        avif: 'image/avif',
      };
      return mimeMap[ext] || 'image/jpeg';
    }
  }
  return 'image/jpeg';
}

export function detectImageMimeTypeFromBytes(
  bytes: Uint8Array,
  fallbackUrl?: string,
): string {
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  ) {
    return 'image/png';
  }

  if (
    bytes.length >= 3 &&
    bytes[0] === 0xff &&
    bytes[1] === 0xd8 &&
    bytes[2] === 0xff
  ) {
    return 'image/jpeg';
  }

  if (
    bytes.length >= 6 &&
    bytes[0] === 0x47 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x38 &&
    (bytes[4] === 0x39 || bytes[4] === 0x37) &&
    bytes[5] === 0x61
  ) {
    return 'image/gif';
  }

  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return 'image/webp';
  }

  if (bytes.length >= 12) {
    const brand = String.fromCharCode(
      bytes[8],
      bytes[9],
      bytes[10],
      bytes[11],
    ).toLowerCase();
    if (brand.includes('avif')) {
      return 'image/avif';
    }
  }

  return fallbackUrl ? inferMimeType(fallbackUrl) : 'image/jpeg';
}

export function imageMimeTypeToLabel(
  mimeType?: string | null,
  fallbackUrl?: string,
): string {
  if (mimeType && MIME_TYPE_LABEL_MAP[mimeType]) {
    return MIME_TYPE_LABEL_MAP[mimeType];
  }
  if (fallbackUrl) {
    const ext = inferMimeType(fallbackUrl);
    if (MIME_TYPE_LABEL_MAP[ext]) {
      return MIME_TYPE_LABEL_MAP[ext];
    }
  }
  return 'IMAGE';
}

async function loadAndDecryptImageToBlob(
  url: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const key = await importAesKey();

  const response = await fetch(url, {
    method: 'GET',
    signal,
  });

  if (!response.ok) {
    throw new Error(`下载加密图片失败: ${response.status} ${response.statusText}`);
  }

  const bytes = new Uint8Array(await response.arrayBuffer());

  if (bytes.length <= 12) {
    throw new Error('加密图片数据无效');
  }

  const iv = bytes.slice(0, 12);
  const encryptedWithTag = bytes.subarray(12);

  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    encryptedWithTag,
  );

  const mimeType = detectImageMimeTypeFromBytes(new Uint8Array(decrypted), url);
  return new Blob([decrypted], { type: mimeType });
}

export function decryptImageToBlobUncached(
  url: string,
  signal?: AbortSignal,
): Promise<Blob> {
  return loadAndDecryptImageToBlob(url, signal);
}

export async function decryptImageToBlob(url: string): Promise<Blob> {
  const cacheKey = String(url || '').trim();
  if (!cacheKey) {
    throw new Error('图片URL为空');
  }

  const cached = _decryptedBlobCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const promise = loadAndDecryptImageToBlob(cacheKey).catch((error) => {
    _decryptedBlobCache.delete(cacheKey);
    throw error;
  });
  _decryptedBlobCache.set(cacheKey, promise);

  if (_decryptedBlobCache.size > MAX_DECRYPTED_BLOB_CACHE_SIZE) {
    const oldestKey = _decryptedBlobCache.keys().next().value;
    if (oldestKey) {
      _decryptedBlobCache.delete(oldestKey);
    }
  }

  return promise;
}

/**
 * 构建加密后的存储文件名
 */
export function buildEncryptedFilename(
  originalFilename: string,
): string {
  const encodedName = encodeBase58(originalFilename);
  const randomSuffix = crypto.randomUUID().replace(/-/g, '').slice(0, 32);
  return `iv_1_${DEFAULT_KEY_ID}_b58_${encodedName}_${randomSuffix}.bin`;
}
